import {ProducerContact, ProducerFillStatus, ProducerList} from '../../types/models';
import {Injectable} from '@angular/core';
import {Category, Product, ProductCleanupOverview, ProductMergeOverview, ProductStoreMergeOverview, ProductMergeResult, ProductNutritionScore, ProductDatabaseFillNext, ProductDatabaseFillResult, ProductStructureStatus, ProductDietTaggingStatus, ProductScope, ProductSeason, ProductSeasonSetting, ProductSeasonCheckStatus, ProductBusinessCheckStatus, ProductLmivStatus, ProductOpenFoodFactsStatus, ProductOpenFactsStatus, OpenFactsSite, ProductNonFoodStatus, ProductNovaStatus, ProductMeanAmountStatus, Gs1Status, InvalidGtinList, InvalidGtinReason, GtinRepairStatus, ProductQuality, ProductReport, ProductListFilter, ProductScopeCounts, ProductCategoryCounts, ImageOptimizeTest, ImageOptimizeRun, ImageBackgroundRun, ImageCheckRun, AiBatch, AiBatchPoll, AiBatchStatus, AiBatchSubmit, ThumbnailRun, Image320Run, ProductDatabaseSetting, ProductSuggestion, ProductSuggestionState} from "../../types/models";
import {ApiService} from "../global/api.service";
import {SortService} from "../core/sort.service";
import {FileService} from "../core/file.service";
import {ValidationService} from "../core/validation.service";

export interface ProductStoreInventory {
  Id: string;
  StoreId: string;
  StoreName: string;
  Warehousing: boolean;
  PurchaseDiscountPercent: number; // discount of the store on the purchasing prices (percent)
  StoreLocation: string;
  StockCount: number;
  Available: boolean;
  Draft: boolean;
  PriceGross: number;        // price of the store, null: the price of the product applies
  PurchasingPrice: number;   // purchasing price of the store, null: the one of the product
  Margin: number;
}

export interface ProductQualityRules {
  Food: boolean;
  FreshProduce: boolean;     // fresh fruit and vegetables: changing producers, no ingredients and nutrition values
  Wine: boolean;             // wine rule: ingredients and nutrition values (at least the energy) despite the alcohol
  FruitWine: boolean;        // fruit wine or cider: no wine rule
  Alcoholic: boolean;        // above 1.2 % alcohol: entered content, spirits/liqueur/beer category or "% vol" in the name
  Missing: string[];
}

export interface ProductImageSource {
  Url: string;
  Type: 'database' | 'chatgpt' | 'photo' | 'cutout' | 'unknown';
  Name: string;              // e.g. 'EDEKA LMIV', 'Eigenes Foto (Fotostation)'
  Source: string;            // address of the original image or null
  At: string;
  Derived?: boolean;         // edited version, the origin is the latest known one of the product
}

export interface ProductStoreOffer {
  StoreId: string;
  StoreName: string;
  Warehousing: boolean;
  InventoryId: string;
  Draft: boolean;
  Available: boolean;
  Offered: boolean;
}

// criteria of product.quality.service.js of the api, without the image
export type CompletionCriterion = 'Price' | 'Tax' | 'Amount' | 'Producer' | 'Ingredients' | 'Nutrition';

export interface ProductCompletion {
  Next: { ProductId: string, Name: string, EAN: string, StoreLocation: string, Food: boolean, Missing: CompletionCriterion[] } | null;
  Total: number;
  Complete: number;
  Remaining: number;
}

@Injectable({
  providedIn: 'root'
})
export class AdminProductService {
  private static readonly DATABASES_KEY = 'kiwi-product-databases';


  constructor(
    private api: ApiService,
    private sortService: SortService,
    private fileService: FileService,
    private validationService: ValidationService
  ) {
  }

  public static getEmptyProduct(ean?: string): Product {
    return {
      Info: {Allergens: [], Ingredients: [], Producer: '', Storage: '', Traces: [], Labels: [], HazardStatements: [], Diets: []},
      Name: null,
        Description: null,
      Unit: null,
      Amount: null,
      MeanAmount: null,
      MeanAmountUnit: null,
      Price: null,
      PurchasingPrice: null,
      Tax: null,
      Categories: [],
      ProductNutritionScores: [],
      IsRecipeProduct: true,
      EAN: ean,
      OrderValue: true,
    }
  }

  getParams(offset?: number, limit?: number) {
    const params: any = {};
    if (offset != null) {
      params.offset = offset;
    }
    if (limit) {
      params.limit = limit;
    }
    return params;
  }

  public async getAll(offset?: number, limit?: number): Promise<Product[]> {
    return this.api.get('admin/product', this.getParams(offset, limit));
  }

  // admin list with filters, Products carry their Margin (markup in percent, null without prices)
  async findAllFiltered(filter: ProductListFilter, offset: number, limit: number): Promise<{ Total: number, AllTotal: number, Products: (Product & { Margin?: number })[] }> {
    const params: any = {offset, limit};
    for (const key of Object.keys(filter || {})) {
      const value = filter[key];
      if (value !== undefined && value !== null && value !== '' && value !== false) {
        params[key] = value;
      }
    }
    return this.api.get('admin/product/list', params);
  }

  // manufacturers by GTIN prefix: correct manufacturer per group, faulty products (see product.producer.service.js)
  async getProducers(filter: { Search?: string, OnlyFaulty?: boolean, WithoutProducer?: boolean, Reload?: boolean }, offset = 0, limit = 50): Promise<ProducerList> {
    const params: any = { Offset: offset, Limit: limit };
    if (filter.Search) {
      params.Search = filter.Search;
    }
    if (filter.OnlyFaulty) {
      params.OnlyFaulty = 'true';
    }
    if (filter.WithoutProducer) {
      params.WithoutProducer = 'true';
    }
    if (filter.Reload) {
      params.Reload = 'true';
    }
    return this.api.get('admin/product/producers', params);
  }

  // address of the manufacturer of the group for its products without address (replace: for all of them)
  // internal contact of the manufacturer (by name), null when unknown
  async getProducerContact(name: string): Promise<ProducerContact | null> {
    return this.api.get('admin/product/producers/contact', { Name: name });
  }

  async saveProducerContact(contact: ProducerContact): Promise<ProducerContact> {
    return this.api.post('admin/product/producers/contact', contact, 'Kontakt gespeichert');
  }

  async applyProducerAddress(prefix: string, address: string, replace = false): Promise<{ Updated: number }> {
    return this.api.post('admin/product/producers/address', { Prefix: prefix, Address: address, Replace: replace });
  }

  // what the filling of manufacturers and addresses would change, start and progress
  // correct: correction of all faulty manufacturers (also other manufacturer, retailer, spelling) instead of the gaps
  async previewProducerFill(correct = false): Promise<{ Producers: number, Addresses: number }> {
    return this.api.get('admin/product/producers/fill/preview', correct ? {Correct: true} : undefined);
  }

  async startProducerFill(correct = false): Promise<ProducerFillStatus> {
    return this.api.post('admin/product/producers/fill', {Correct: correct});
  }

  async getProducerFillStatus(): Promise<ProducerFillStatus> {
    return this.api.get('admin/product/producers/fill');
  }

  async applyProducer(prefix: string, producer: string, productIds: string[] = null): Promise<{ Updated: number }> {
    return this.api.post('admin/product/producers/apply', { Prefix: prefix, Producer: producer, ProductIds: productIds });
  }

  async findAll(filter: string, offset?: number, limit?: number): Promise<Product[]> {
    if (!filter || filter.length === 0) {
      return this.getAll(offset, limit);
    }
    return this.api.get(`admin/product/by-filter/${filter}`, this.getParams(offset, limit));
  }

  async findAllByFilter(searchTerm: string): Promise<Product[]> {
    return this.findAllByFilterImpl(searchTerm);
  }

  async findAllRecipeProductsByFilter(searchTerm: string, excludedIds: string[] = []): Promise<Product[]> {
    const params = { Not: excludedIds };
    return this.findAllByFilterImpl(searchTerm, params, '/recipe-product');
  }

  async findAllProductAlternativesByFilter(searchTerm: string, refItem: Product, productAlternatives: Product[] = []): Promise<Product[]> {
    const params: any = {};
    const excludedIds = [];
    if (refItem) {
      params.Unit = refItem.Unit;
      params.MeanAmountUnit = refItem.MeanAmountUnit;
      excludedIds.push(refItem.Id);
    }
    if (productAlternatives && productAlternatives.length > 0) {
      productAlternatives.forEach(product => excludedIds.push(product.Id));
    }
    if (excludedIds.length > 0) {
      params.Not = excludedIds;
    }
    return this.findAllByFilterImpl(searchTerm, params, '/recipe-product');
  }

  private async findAllByFilterImpl(searchTerm: string, additionalParams: any = {}, subRoute: string = ''):  Promise<Product[]> {
    const filteredSearchTerm = (searchTerm || '').trim().toLowerCase();
    if (filteredSearchTerm.length === 0) {
      return [];
    }
    const params = Object.assign({}, {
      Name: filteredSearchTerm
    }, additionalParams);
    return this.api.get('admin/product/by-filter' + subRoute, { filter: JSON.stringify(params) });
  }

  public async getCleanupOverview(): Promise<ProductCleanupOverview> {
    return this.api.get('admin/product/cleanup');
  }

  public async getMergeMatches(): Promise<ProductMergeOverview> {
    return this.api.get('admin/product/cleanup/merge');
  }

  // the products of the old store (by hand, without GTIN) with their candidates in the imported store
  public async getStoreMergeMatches(sourceStoreId: string, targetStoreId: string): Promise<ProductStoreMergeOverview> {
    return this.api.get('admin/product/cleanup/merge/stores', {SourceStoreId: sourceStoreId, TargetStoreId: targetStoreId});
  }

  // pairs: the old product (KeepId) stays and gets the GTIN of the new product (MergeId)
  public async mergeProducts(pairs: { KeepId: string, MergeId: string }[]): Promise<ProductMergeResult> {
    return this.api.post('admin/product/cleanup/merge', {Pairs: pairs}, 'Produkte zusammengeführt');
  }

  public async deleteProducts(ids: string[]): Promise<{ Deleted: number }> {
    return this.api.post('admin/product/cleanup/delete', {Ids: ids}, 'Produkte gelöscht');
  }

  // expectedCount is the number confirmed by the user, the api rejects the deletion when it changed
  public async deleteDrafts(expectedCount: number): Promise<{ Deleted: number }> {
    return this.api.post('admin/product/cleanup/drafts', {ExpectedCount: expectedCount}, 'Entwürfe gelöscht');
  }

  public async deleteWithoutStore(expectedCount: number): Promise<{ Deleted: number }> {
    return this.api.post('admin/product/cleanup/without-store', {ExpectedCount: expectedCount}, 'Produkte ohne Filiale gelöscht');
  }

  public async getSuggestionState(productId: string): Promise<ProductSuggestionState> {
    return this.api.get(`admin/product/suggestion/product/${productId}`);
  }

  // asks ChatGPT in the background, getSuggestionState returns the result after about one minute
  // webQuery: ChatGPT evaluates the first search result page of the query instead of searching the web itself
  public async startSuggestion(productId: string, webQuery: string = null): Promise<ProductSuggestionState> {
    return this.api.post(`admin/product/suggestion/product/${productId}`, webQuery ? {WebQuery: webQuery} : {});
  }

  // searches the connected product databases (e.g. EDEKA PIM) by the GTIN, the result is ready immediately
  public async lookupSuggestion(productId: string): Promise<ProductSuggestionState> {
    return this.api.post(`admin/product/suggestion/product/${productId}/database`, {Databases: this.getSelectedDatabases()});
  }

  // selected product databases (keys in the order of the search), kept in the local storage; empty: all
  public getSelectedDatabases(): string[] {
    try {
      const keys = JSON.parse(localStorage.getItem(AdminProductService.DATABASES_KEY) || '[]');
      return Array.isArray(keys) ? keys : [];
    } catch (e) {
      return [];
    }
  }

  public setSelectedDatabases(keys: string[]) {
    try {
      localStorage.setItem(AdminProductService.DATABASES_KEY, JSON.stringify(keys || []));
    } catch (e) {
    }
  }

  // only products in the inventory of the store and in the categories (with sub categories)
  public async getNextIncompleteProduct(storeId: string, scope: ProductScope, databases: string[]): Promise<ProductDatabaseFillNext> {
    return this.api.get('admin/product/database/fill/next', {
      StoreId: storeId, CategoryIds: scope.CategoryIds.join(','), AllCategories: scope.AllCategories, Databases: databases.join(',')
    });
  }

  // collects the data of free sources and lets ChatGPT structure it without web search, only empty fields are filled
  // webSearch: ChatGPT completes the data by a web search (more expensive), also for products without data
  public async startStructure(storeId: string, scope: ProductScope, limit: number, webSearch: boolean, openFoodFacts = true, onlyWithImage = false, fields: string[] = []): Promise<ProductStructureStatus> {
    return this.api.post('admin/product/structure/start', {StoreId: storeId, ...scope, Limit: limit, WebSearch: webSearch, OpenFoodFacts: openFoodFacts, OnlyWithImage: onlyWithImage, Fields: fields});
  }

  // sends the queued items of the nightly batch now instead of at 22:00 (in the background)
  public async startAiBatchSubmit(): Promise<AiBatchSubmit> {
    return this.api.post('admin/product/ai-batch/submit', {});
  }

  public async getAiBatchSubmit(): Promise<AiBatchSubmit> {
    return this.api.get('admin/product/ai-batch/submit');
  }

  public async getAiBatchStatus(): Promise<AiBatchStatus> {
    return this.api.get('admin/product/ai-batch/status');
  }

  public async getAiBatches(): Promise<AiBatch[]> {
    return this.api.get('admin/product/ai-batch/batches');
  }

  // fetches and enters the results of the finished batches now (in the background)
  public async startAiBatchPoll(): Promise<AiBatchPoll> {
    return this.api.post('admin/product/ai-batch/poll', {});
  }

  public async getAiBatchPoll(): Promise<AiBatchPoll> {
    return this.api.get('admin/product/ai-batch/poll');
  }

  public async getStructureStatus(): Promise<ProductStructureStatus> {
    return this.api.get('admin/product/structure/status');
  }

  // products without data in any source, they can only be refined with web search in the product
  public async getStructureWithoutData(storeId: string, scope: ProductScope): Promise<{ Id: string, Name: string, EAN: string }[]> {
    return this.api.get('admin/product/structure/without-data', {
      StoreId: storeId, CategoryIds: scope.CategoryIds.join(','), AllCategories: scope.AllCategories
    });
  }

  // ChatGPT tags the diets (only positive tags, e.g. 'laktosefrei') of the products with ingredients
  public async startDietTagging(storeId: string, scope: ProductScope, limit: number): Promise<ProductDietTaggingStatus> {
    return this.api.post('admin/product/diet/start', {StoreId: storeId, ...scope, Limit: limit});
  }

  public async getReports(status: string): Promise<ProductReport[]> {
    return this.api.get('admin/product/report', {Status: status});
  }

  public async countOpenReports(): Promise<number> {
    return this.api.get('admin/product/report/count');
  }

  // apply: the (edited) value is written into the product, a promised reward is given as coupon
  public async acceptReport(id: string, apply: boolean, value: string): Promise<ProductReport> {
    return this.api.post(`admin/product/report/${id}/accept`, {Apply: apply, Value: value}, 'Meldung übernommen');
  }

  public async rejectReport(id: string): Promise<ProductReport> {
    return this.api.post(`admin/product/report/${id}/reject`, {}, 'Meldung abgelehnt');
  }

  public async getCategoryCounts(storeId: string, databases: string[]): Promise<ProductCategoryCounts[]> {
    return this.api.get('admin/product/scope/category-counts', {StoreId: storeId, Databases: databases.join(',')});
  }

  // replaces only the images of the product (the other fields stay unchanged)
  // set by hand: the automatic check of corporate products keeps it afterwards
  public async setBusinessOnly(productId: string, businessOnly: boolean): Promise<Product> {
    return this.api.post('admin/product', {Id: productId, BusinessOnly: businessOnly},
      businessOnly ? 'Für Großkunden vorgesehen' : 'Für alle Kunden sichtbar');
  }

  // deleted product with the stock entries deleted together with it, the categories have to be assigned again
  public async restore(productId: string): Promise<{ Id: string, Inventories: number }> {
    return this.api.post('admin/product/' + productId + '/restore', {}, 'Produkt wiederhergestellt');
  }

  // disabled: hidden from customers and statistics, ignored by the price list import
  public async setDisabled(productId: string, disabled: boolean): Promise<Product> {
    return this.api.post('admin/product', {Id: productId, Disabled: disabled}, disabled ? 'Produkt deaktiviert' : 'Produkt aktiviert');
  }

  public async updateImages(productId: string, images: string[]): Promise<Product> {
    return this.api.post('admin/product', {Id: productId, Images: images}, 'Bild gelöscht');
  }

  // ChatGPT optimizes the images of 10 random products of the scope, only to compare before and after
  public async startImageTest(storeId: string, scope: ProductScope): Promise<ImageOptimizeTest> {
    return this.api.post('admin/product/image-test/start', {StoreId: storeId, ...scope});
  }

  public async getImageTest(): Promise<ImageOptimizeTest> {
    return this.api.get('admin/product/image-test');
  }

  // cancels a background run, e.g. 'image-optimize-test', 'product-structure'
  public async cancelJob(key: string): Promise<{ Cancelled: boolean }> {
    return this.api.post(`admin/product/job/${key}/cancel`, {}, 'Lauf abgebrochen');
  }

  // force: also replace existing thumbnails
  public async startThumbnails(storeId: string, scope: ProductScope, limit: number, force: boolean): Promise<ThumbnailRun> {
    return this.api.post('admin/product/thumbnails/start', {StoreId: storeId, ...scope, Limit: limit, Force: force});
  }

  public async getThumbnails(storeId?: string, scope?: ProductScope): Promise<ThumbnailRun> {
    return this.api.get('admin/product/thumbnails', storeId && scope ? {
      StoreId: storeId, CategoryIds: scope.CategoryIds.join(','), AllCategories: scope.AllCategories
    } : {});
  }

  // 320 pixel webp versions of all product images, force: also replace existing ones
  public async startImages320(force: boolean): Promise<Image320Run> {
    return this.api.post('admin/product/images-320/start', {Force: force});
  }

  public async getImages320(): Promise<Image320Run> {
    return this.api.get('admin/product/images-320');
  }

  // number of products with more than one image
  public async countMultipleImages(): Promise<{ Count: number }> {
    return this.api.get('admin/product/images/multiple');
  }

  // every product keeps only one image: the optimized one, if there is one, otherwise the first
  public async reduceToOneImage(): Promise<{ Reduced: number }> {
    return this.api.post('admin/product/images/reduce', {});
  }

  public async startImageRun(storeId: string, scope: ProductScope, limit: number): Promise<ImageOptimizeRun> {
    return this.api.post('admin/product/image-run/start', {StoreId: storeId, ...scope, Limit: limit});
  }

  // progress of the full run, with storeId and scope also the number of open products of the selection
  public async getImageRun(storeId?: string, scope?: ProductScope): Promise<ImageOptimizeRun> {
    return this.api.get('admin/product/image-run', storeId && scope ? {
      StoreId: storeId, CategoryIds: scope.CategoryIds.join(','), AllCategories: scope.AllCategories
    } : {});
  }

  // the white background of all optimized images becomes transparent (server run, without ChatGPT)
  public async startImageBackgroundRun(): Promise<ImageBackgroundRun> {
    return this.api.post('admin/product/image-background-run/start', {});
  }

  public async getImageBackgroundRun(): Promise<ImageBackgroundRun> {
    return this.api.get('admin/product/image-background-run', {});
  }

  // full run of the image forensics (without optimizing) for the selection
  // all: also the images, which were checked before
  public async startImageCheckRun(storeId: string, scope: ProductScope, limit: number, all: boolean = false): Promise<ImageCheckRun> {
    return this.api.post('admin/product/image-check-run/start', {StoreId: storeId, ...scope, Limit: limit, All: all});
  }

  public async getImageCheckRun(storeId?: string, scope?: ProductScope): Promise<ImageCheckRun> {
    return this.api.get('admin/product/image-check-run', storeId && scope ? {
      StoreId: storeId, CategoryIds: scope.CategoryIds.join(','), AllCategories: scope.AllCategories
    } : {});
  }

  public async getScopeCounts(storeId: string, scope: ProductScope): Promise<ProductScopeCounts> {
    return this.api.get('admin/product/scope/counts', {
      StoreId: storeId, CategoryIds: scope.CategoryIds.join(','), AllCategories: scope.AllCategories
    });
  }

  public async getSeasons(): Promise<ProductSeason[]> {
    return this.api.get('admin/product/season/list');
  }

  public async getSeasonSettings(): Promise<ProductSeasonSetting[]> {
    return this.api.get('admin/product/season/settings');
  }

  // without Key a new season is created; a fixed season without Start and End gets its default window again
  public async saveSeason(season: { Key?: string, Name?: string, Start: string, End: string }): Promise<ProductSeasonSetting[]> {
    return this.api.post('admin/product/season/settings', season, 'Saison gespeichert');
  }

  public async deleteSeason(key: string): Promise<ProductSeasonSetting[]> {
    return this.api.delete('admin/product/season/settings/' + encodeURIComponent(key), 'Saison gelöscht');
  }

  public async getSeasonProducts(key: string): Promise<Product[]> {
    return this.api.get('admin/product/season/settings/' + encodeURIComponent(key) + '/products');
  }

  public async setSeasonProducts(key: string, productIds: string[], add: boolean): Promise<{ Changed: number }> {
    return this.api.post('admin/product/season/settings/' + encodeURIComponent(key) + '/products', {ProductIds: productIds, Add: add},
      add ? 'Produkte hinzugefügt' : 'Produkte entfernt');
  }

  // ChatGPT identifies the seasonal products, which were never checked
  public async startSeasonCheck(storeId: string, scope: ProductScope, limit: number): Promise<ProductSeasonCheckStatus> {
    return this.api.post('admin/product/season/start', {StoreId: storeId, ...scope, Limit: limit});
  }

  public async getSeasonCheckStatus(): Promise<ProductSeasonCheckStatus> {
    return this.api.get('admin/product/season/status');
  }

  public async startBusinessCheck(storeId: string, scope: ProductScope, limit: number): Promise<ProductBusinessCheckStatus> {
    return this.api.post('admin/product/business/start', {StoreId: storeId, ...scope, Limit: limit});
  }

  // quality of the selection (store and categories), of all products of the store or of all products
  // next product of the store with missing mandatory information (without image), exclude: done in this session
  public async getNextCompletion(storeId: string, exclude: string[]): Promise<ProductCompletion> {
    return this.api.post('admin/product/completion/next/' + storeId, {Exclude: exclude});
  }

  public async getQuality(storeId?: string, scope?: ProductScope): Promise<ProductQuality> {
    const params: any = storeId ? {StoreId: storeId} : {};
    if (storeId && scope) {
      Object.assign(params, {CategoryIds: scope.CategoryIds.join(','), AllCategories: scope.AllCategories});
    }
    return this.api.get('admin/product/quality', params);
  }

  // publishes the drafts of the quality view with all mandatory information, expectedCount: number shown to the admin
  public async publishReadyDrafts(expectedCount: number, storeId?: string, scope?: ProductScope): Promise<{ Published: number }> {
    const body: any = {ExpectedCount: expectedCount};
    if (storeId) {
      Object.assign(body, {StoreId: storeId}, scope ? {CategoryIds: scope.CategoryIds, AllCategories: scope.AllCategories} : {});
    }
    return this.api.post('admin/product/quality/publish', body, 'Entwürfe veröffentlicht');
  }

  public async startLmiv(storeId: string, scope: ProductScope, limit: number): Promise<ProductLmivStatus> {
    return this.api.post('admin/product/lmiv/start', {StoreId: storeId, ...scope, Limit: limit});
  }

  // all open food products, also foreign brands (without store, categories and limit)
  public async startLmivAll(): Promise<ProductLmivStatus> {
    return this.api.post('admin/product/lmiv/start', {All: true});
  }

  // Verified by GS1: status, start (all products, as many as the api allows) and acceptance of the terms of use
  public async getGs1Status(): Promise<Gs1Status> {
    return this.api.get('admin/product/gs1/status');
  }

  public async startGs1(): Promise<Gs1Status> {
    return this.api.post('admin/product/gs1/start', {});
  }

  // takes the stored records of GS1 again into the empty fields of the products, without requests
  public async startGs1Apply(): Promise<Gs1Status> {
    return this.api.post('admin/product/gs1/apply', {});
  }

  public async acceptGs1Terms(): Promise<{ Accepted: boolean }> {
    return this.api.post('admin/product/gs1/terms', {}, 'Nutzungsbedingungen akzeptiert');
  }

  // mean amount of pieces (of one piece or of all pieces) of all products by the cheapest model
  public async startMeanAmount(limit: number): Promise<ProductMeanAmountStatus> {
    return this.api.post('admin/product/mean-amount/start', {Limit: limit});
  }

  public async getMeanAmountStatus(): Promise<ProductMeanAmountStatus> {
    return this.api.get('admin/product/mean-amount/status');
  }

  // NOVA group of the food products of the selection by the cheapest model
  public async startNova(storeId: string, scope: ProductScope, limit: number): Promise<ProductNovaStatus> {
    return this.api.post('admin/product/nova/start', {StoreId: storeId, ...scope, Limit: limit});
  }

  public async getNovaStatus(storeId?: string, scope?: ProductScope): Promise<ProductNovaStatus> {
    return this.api.get('admin/product/nova/status', storeId && scope ? {
      StoreId: storeId, CategoryIds: scope.CategoryIds.join(','), AllCategories: scope.AllCategories
    } : {});
  }

  // products of the selection without valid GTIN, reasons: filter (empty: all)
  public async getInvalidGtins(storeId: string, scope: ProductScope, reasons: InvalidGtinReason[] = []): Promise<InvalidGtinList> {
    return this.api.get('admin/product/gtin/invalid', {
      StoreId: storeId, CategoryIds: scope.CategoryIds.join(','), AllCategories: scope.AllCategories, Reasons: reasons.join(',')
    });
  }

  // searches the GTIN by the name: the selected products (productIds) or all with the reasons, again: also searched ones
  public async startGtinRepair(storeId: string, scope: ProductScope, limit: number, reasons: InvalidGtinReason[], productIds: string[], again: boolean): Promise<GtinRepairStatus> {
    return this.api.post('admin/product/gtin/repair', {StoreId: storeId, ...scope, Limit: limit, Reasons: reasons, ProductIds: productIds, Again: again});
  }

  public async getGtinRepairStatus(): Promise<GtinRepairStatus> {
    return this.api.get('admin/product/gtin/repair');
  }

  // recognition of non food products of the selection
  public async startNonFood(storeId: string, scope: ProductScope, limit: number): Promise<ProductNonFoodStatus> {
    return this.api.post('admin/product/non-food/start', {StoreId: storeId, ...scope, Limit: limit});
  }

  public async getNonFoodStatus(storeId?: string, scope?: ProductScope): Promise<ProductNonFoodStatus> {
    return this.api.get('admin/product/non-food/status', storeId && scope ? {
      StoreId: storeId, CategoryIds: scope.CategoryIds.join(','), AllCategories: scope.AllCategories
    } : {});
  }

  // Open Food Facts for the products of the selection: one request fills all empty fields (also the scores)
  public async startOpenFoodFacts(storeId: string, scope: ProductScope, limit: number): Promise<ProductOpenFoodFactsStatus> {
    return this.api.post('admin/product/open-food-facts/start', {StoreId: storeId, ...scope, Limit: limit});
  }

  public async getOpenFoodFactsStatus(storeId?: string, scope?: ProductScope): Promise<ProductOpenFoodFactsStatus> {
    return this.api.get('admin/product/open-food-facts/status', storeId && scope ? {
      StoreId: storeId, CategoryIds: scope.CategoryIds.join(','), AllCategories: scope.AllCategories
    } : {});
  }

  public async startOpenFacts(site: OpenFactsSite, storeId: string, scope: ProductScope, limit: number): Promise<ProductOpenFactsStatus> {
    return this.api.post('admin/product/open-facts/' + site + '/start', {StoreId: storeId, ...scope, Limit: limit});
  }

  public async getOpenFactsStatus(site: OpenFactsSite, storeId?: string, scope?: ProductScope): Promise<ProductOpenFactsStatus> {
    return this.api.get('admin/product/open-facts/' + site + '/status', storeId && scope ? {
      StoreId: storeId, CategoryIds: scope.CategoryIds.join(','), AllCategories: scope.AllCategories
    } : {});
  }

  // Icecat for all non food products, runs on the server until each one was searched once
  public async startIcecat(): Promise<ProductOpenFactsStatus> {
    return this.api.post('admin/product/icecat/start', {});
  }

  // progress of the run and the number of non food products, which were not searched in Icecat yet
  public async getIcecatStatus(): Promise<ProductOpenFactsStatus> {
    return this.api.get('admin/product/icecat/status', {});
  }

  // progress of the run, with storeId and scope also the number of open products of the selection
  public async getLmivStatus(storeId?: string, scope?: ProductScope): Promise<ProductLmivStatus> {
    return this.api.get('admin/product/lmiv/status', storeId && scope ? {
      StoreId: storeId, CategoryIds: scope.CategoryIds.join(','), AllCategories: scope.AllCategories
    } : {});
  }

  public async getBusinessCheckStatus(): Promise<ProductBusinessCheckStatus> {
    return this.api.get('admin/product/business/status');
  }

  public async getDietTaggingStatus(): Promise<ProductDietTaggingStatus> {
    return this.api.get('admin/product/diet/status');
  }


  // searches the product in the product databases and fills only its empty fields
  // the databases are searched one after another, each fills the fields which are still empty
  public async fillFromDatabases(productId: string, databases: string[]): Promise<ProductDatabaseFillResult> {
    return this.api.post(`admin/product/database/fill/${productId}`, {Databases: databases});
  }

  // user name and password, the api tests the login at once and logs in by itself later
  public async setDatabaseCredentials(key: string, username: string, password: string): Promise<ProductDatabaseSetting[]> {
    return this.api.post('admin/product/database/credentials', {Key: key, Username: username, Password: password},
      username ? 'Zugangsdaten gespeichert, Anmeldung erfolgreich' : 'Zugangsdaten entfernt');
  }

  public async getDatabaseSettings(): Promise<ProductDatabaseSetting[]> {
    return this.api.get('admin/product/database/setting');
  }

  // an empty token removes the stored token
  public async setDatabaseToken(key: string, token: string): Promise<ProductDatabaseSetting[]> {
    return this.api.post('admin/product/database/setting', {Key: key, Token: token}, token ? 'Token gespeichert' : 'Token entfernt');
  }

  public async acceptSuggestion(suggestion: ProductSuggestion, fields: string[]): Promise<any> {
    return this.api.post(`admin/product/suggestion/${suggestion.Id}/accept`, {Fields: fields});
  }

  public async rejectSuggestion(suggestion: ProductSuggestion): Promise<any> {
    return this.api.post(`admin/product/suggestion/${suggestion.Id}/reject`, {});
  }

  public async existsEAN(EAN: string): Promise<boolean> {
    return this.api.get(`admin/product/EAN/exists/${EAN}`);
  }

  // misread scan of the photo station: the images (also the photos waiting in the nightly batch) go to the original
  // product, the placeholder "Neues Produkt <GTIN>" is deleted afterwards
  public async moveImages(sourceId: string, targetId: string): Promise<{Moved: number; Pending: number; SourceDeleted: boolean; Target: {Id: string; Name: string; EAN: string}}> {
    return this.api.post(`admin/product/${sourceId}/move-images`, {TargetId: targetId});
  }

  public async getByEAN(EAN: string): Promise<Product> {
    return this.getByPath(`admin/product/EAN/${EAN}`);
  }

  public async getByEANLightWeight(EAN: string): Promise<Product> {
    return this.getByPath(`admin/product/EAN/lightweight/${EAN}`);
  }

  // storage location, stock and availability of the product in each store
  // first web search result of the query (address of the shop page) or null
  public async findFirstHit(query: string): Promise<{Url: string}> {
    return this.api.get('admin/product/web/first-hit', {Query: query});
  }

  // completeness rules of the product: food, fresh fruit and vegetables, wine (by name and categories)
  public async getQualityRules(Id: string): Promise<ProductQualityRules> {
    return this.api.get(`admin/product/quality-rules/${Id}`);
  }

  // origin of the images of the product, in the order of the images
  public async getImageSources(Id: string): Promise<ProductImageSource[]> {
    return this.api.get(`admin/product/image-sources/${Id}`);
  }

  // all stores with the state of the product (offered: stock entry, no draft)
  public async getStores(Id: string): Promise<ProductStoreOffer[]> {
    return this.api.get(`admin/product/stores/${Id}`);
  }

  // offers the product in exactly these stores (the others keep their entry as draft)
  public async setStores(Id: string, storeIds: string[]): Promise<ProductStoreOffer[]> {
    return this.api.post(`admin/product/stores/${Id}`, {StoreIds: storeIds});
  }

  // selling price from the product list: without storeId the price of the product, with storeId the own price of the
  // store (priceGross null: the store uses the price of the product again)
  // discountStoreId: without storeId the returned margin uses the purchase discount of this store (like the list)
  public async savePrice(productId: string, priceGross: number, storeId: string = null, discountStoreId: string = null):
    Promise<{ PriceGross: number, StorePriceGross?: number, OwnStorePrice?: boolean, Margin: number }> {
    return this.api.post(`admin/product/price/${productId}`, {PriceGross: priceGross, StoreId: storeId, DiscountStoreId: discountStoreId});
  }

  public async getInventories(Id: string): Promise<ProductStoreInventory[]> {
    return this.api.get(`admin/product/inventories/${Id}`);
  }

  // location and price of the product in one store, PriceGross null: the price of the product applies
  public async saveInventory(productId: string, inventoryId: string, values: { StoreLocation: string, PriceGross: number }):
    Promise<Pick<ProductStoreInventory, 'Id' | 'StoreLocation' | 'PriceGross' | 'PurchasingPrice' | 'Margin'>> {
    return this.api.post(`admin/product/inventory/${productId}/${inventoryId}`, values, 'Filiale gespeichert');
  }

  public async get(Id: string): Promise<Product> {
    return this.getByPath(`admin/product/${Id}`);
  }

  private async getByPath(path: string) {
    const product = await this.api.get(path);
    if (product && product.Categories) {
      this.sortService.sortByField(product.Categories, 'Name');
    }
    return product;
  }

  public async save(product: Product, newImages: File[]): Promise<Product> {
    if (product.Draft && this.hasNoName(product)) {
      return null;
    } else if (!product.Draft && (this.hasErrors(product) || this.productNutritionScoresHaveErrors(product.ProductNutritionScores))) {
      return null;
    }
    product.Info.Allergens = product.Info.Allergens.filter(text => text != null && text.length > 0);
    product.Info.Ingredients = product.Info.Ingredients.filter(text => text != null && text.length > 0);
    await this.fileService.saveFiles(product, newImages, 'Product');
    return this.api.post('admin/product', product, 'Speichern erfolgreich.');
  }

  private productNutritionScoresHaveErrors(productNutritionScores: ProductNutritionScore[]): boolean {
    const notEmptyFields = [
      {field: 'NutritionScore', name: 'Nährwert-Typ'},
      {field: 'Value', name: 'Nährwert-Menge'},
      {field: 'Value', name: 'Nährwert-Menge', fctName: 'hasNegativeValues'}
    ];
    return this.validationService.arrayHasErrors(productNutritionScores, notEmptyFields);
  }

  private hasNoName(product: Product): boolean {
    const notEmptyFields = [
      {field: 'Name', name: 'Name'}
    ];
    return this.validationService.hasEmptyFields(product, notEmptyFields)
  }

  private hasErrors(product: Product): boolean {
    const notEmptyFields = [
      {field: 'Name', name: 'Name'},
      {field: 'PriceGross', name: 'Brutto-Preis'},
      {field: 'Amount', name: 'Menge'},
      {field: 'Unit', name: 'Einheit'},
      {field: 'Tax', name: 'Mehrwertsteuer'}
    ];
    if (product.Unit === 'Stk' && product.IsRecipeProduct) {
      notEmptyFields.push({field: 'MeanAmount', name: 'Durchschnittliche Menge'});
      notEmptyFields.push({field: 'MeanAmountUnit', name: 'Einheit der durchschnittlichen Menge'});
    }
    return this.validationService.hasEmptyFields(product, notEmptyFields);
  }

  public async delete(product: Product): Promise<Product> {
    if (product.Id) {
      return this.api.delete(`admin/product/${product.Id}`);
    }
    return product;
  }

  public async inUse(product: Product): Promise<{ InUse: boolean, Message: string }> {
    const inUse: { Recipes?: number } = product.Id ? await this.api.get((`admin/product/in-use/${product.Id}`)) : {};
    const response = {InUse: false, Message: ''};
    if (inUse.Recipes) {
      response.InUse = true;
      response.Message = 'Das Produkt wird bereits in ' + (inUse.Recipes === 1 ? 'einem Rezept' : inUse.Recipes + ' Rezepten') + ' verwendet. '
        + 'Wenn das Produkt gelöscht wird, wird es ebenfalls aus allen Rezepten gelöscht.'
    }
    return response;
  }

  public async loadProductInformation(ean: string): Promise<Product> {
    return this.api.get(`admin/product/import/find/${ean}`);
  }

  public async importProduct(product: Product, setting: any, imageSetting: string, create: boolean = true): Promise<Product> {
    const updateSetting: any = JSON.parse(JSON.stringify(setting));
    updateSetting['Images'] = { Value : imageSetting };
    updateSetting['Create'] = create;
    const item = {
      Product: product,
      Settings: updateSetting
    }
    return this.api.post('admin/product/import', item);
  }

}
